import { useState } from 'react';
import { addDays, addMonths, eachDayOfInterval, format, isAfter, isBefore, isValid, parseISO, startOfMonth } from 'date-fns';
import XLSX from 'xlsx-js-style';
import pptxgen from 'pptxgenjs';
import {
  BarChart3,
  FileSpreadsheet,
  Presentation,
  Euro,
  Wallet,
  TrendingUp,
  Percent,
  CalendarClock,
  Clock,
  Target,
  GitCompare,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';

const HOURS_PER_DAY = 8;

const parseDate = function(value) {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

const startOfDay = function(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const isWorkday = function(date) {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const maxDate = function(dates) {
  let result = null;
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    if (!date) continue;
    if (!result || isAfter(date, result)) result = date;
  }
  return result;
};

const minDate = function(dates) {
  let result = null;
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    if (!date) continue;
    if (!result || isBefore(date, result)) result = date;
  }
  return result;
};

const getWeekStartFromKey = function(weekKey) {
  const parts = String(weekKey || '').split('-W');
  if (parts.length !== 2) return null;
  const year = Number(parts[0]);
  const weekNum = Number(parts[1]);
  if (!Number.isFinite(year) || !Number.isFinite(weekNum)) return null;

  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay();
  const jan4Monday = new Date(year, 0, 4 - (jan4Day === 0 ? 6 : jan4Day - 1));
  const weekStart = new Date(jan4Monday);
  weekStart.setDate(jan4Monday.getDate() + (weekNum - 1) * 7);
  return startOfDay(weekStart);
};

const countWorkdays = function(start, end) {
  if (!start || !end || isAfter(start, end)) return 0;
  const days = eachDayOfInterval({ start: start, end: end });
  let count = 0;
  for (let i = 0; i < days.length; i++) {
    if (isWorkday(days[i])) count += 1;
  }
  return count;
};

const addMonthValue = function(months, date, hours) {
  const key = format(date, 'yyyy-MM');
  if (!months[key]) {
    months[key] = { key: key, label: format(date, 'MM/yyyy'), hours: 0 };
  }
  months[key].hours += hours;
};

const initMonthlyBuckets = function(today, maxEndDate) {
  const buckets = {};
  if (!maxEndDate || isBefore(maxEndDate, today)) return buckets;

  let cursor = startOfMonth(today);
  const end = startOfMonth(maxEndDate);
  while (!isAfter(cursor, end)) {
    buckets[format(cursor, 'yyyy-MM')] = {
      key: format(cursor, 'yyyy-MM'),
      label: format(cursor, 'MM/yyyy'),
      hours: 0
    };
    cursor = addMonths(cursor, 1);
  }
  return buckets;
};

const formatNumber = function(value, digits) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return value.toLocaleString('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const formatCurrency = function(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatPercent = function(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return formatNumber(value, 1) + '%';
};

const rawNumber = function(value) {
  return value === null || value === undefined || !Number.isFinite(value) ? '' : value;
};

const getSignColor = function(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return value >= 0 ? 'text-emerald-600' : 'text-rose-600';
};

const KpiCard = function({ icon: Icon, label, value, valueClassName, accentClass, footer }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2">
        <div className={'rounded-full p-2 flex items-center justify-center ' + (accentClass || 'bg-slate-100 text-slate-600')}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-xs text-muted-foreground leading-tight">{label}</div>
      </div>
      <div className={'text-2xl font-semibold mt-2 ' + (valueClassName || '')}>{value}</div>
      {footer && <div className="text-xs text-muted-foreground mt-1">{footer}</div>}
    </div>
  );
};

const calculateAnalysis = function(projects, tasks, resources) {
  const today = startOfDay(new Date());
  const resourceMap = {};
  const projectRows = [];
  const projectEndDates = [];
  const tasksByProject = {};
  let crossFutureHours = 0;
  let crossEstimatedDays = 0;
  let crossRealHours = 0;
  let crossSaleAmount = 0;
  let crossTotalCost = 0;

  for (let i = 0; i < resources.length; i++) {
    resourceMap[resources[i].id] = resources[i];
  }

  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    if (!tasksByProject[task.projectId]) tasksByProject[task.projectId] = [];
    tasksByProject[task.projectId].push(task);
  }

  for (let p = 0; p < projects.length; p++) {
    const project = projects[p];
    const projectStart = parseDate(project.startDate);
    const projectEnd = parseDate(project.endDate);
    if (projectEnd) projectEndDates.push(projectEnd);
  }

  const maxEndDate = maxDate(projectEndDates);
  const monthlyBuckets = initMonthlyBuckets(today, maxEndDate);

  for (let p = 0; p < projects.length; p++) {
    const project = projects[p];
    const projectStart = parseDate(project.startDate);
    const projectEnd = parseDate(project.endDate);
    const projectTasks = tasksByProject[project.id] || [];
    const resourceTotals = {};
    let realHours = 0;
    let futureHours = 0;

    for (let t = 0; t < projectTasks.length; t++) {
      const task = projectTasks[t];
      const taskStart = parseDate(task.startDate);
      const taskEnd = parseDate(task.endDate);
      const allocations = task.resourceAllocations || [];
      if (!taskStart || !taskEnd || !projectStart || !projectEnd) continue;

      for (let a = 0; a < allocations.length; a++) {
        const allocation = allocations[a];
        if (!resourceMap[allocation.resourceId]) continue;
        const weeklyHours = allocation.weeklyHours || {};
        const weekKeys = Object.keys(weeklyHours);
        if (!resourceTotals[allocation.resourceId]) {
          resourceTotals[allocation.resourceId] = { hours: 0, futureHours: 0 };
        }

        for (let w = 0; w < weekKeys.length; w++) {
          const weekKey = weekKeys[w];
          const hours = Number(weeklyHours[weekKey]) || 0;
          if (hours <= 0) continue;

          const weekStart = getWeekStartFromKey(weekKey);
          if (!weekStart) continue;
          const weekEnd = addDays(weekStart, 6);
          const totalStart = maxDate([projectStart, taskStart, weekStart]);
          const totalEnd = minDate([projectEnd, taskEnd, weekEnd]);
          if (!totalStart || !totalEnd || isAfter(totalStart, totalEnd)) continue;

          realHours += hours;
          resourceTotals[allocation.resourceId].hours += hours;

          const futureStart = maxDate([today, projectStart, taskStart, weekStart]);
          const futureEnd = minDate([projectEnd, taskEnd, weekEnd]);
          if (!futureStart || !futureEnd || isAfter(futureStart, futureEnd)) continue;

          const totalWorkdays = countWorkdays(totalStart, totalEnd);
          const futureWorkdays = countWorkdays(futureStart, futureEnd);
          if (totalWorkdays <= 0 || futureWorkdays <= 0) continue;

          const futureWeekHours = hours * (futureWorkdays / totalWorkdays);
          const dailyHours = hours / totalWorkdays;
          futureHours += futureWeekHours;
          resourceTotals[allocation.resourceId].futureHours += futureWeekHours;

          const futureDays = eachDayOfInterval({ start: futureStart, end: futureEnd });
          for (let d = 0; d < futureDays.length; d++) {
            if (!isWorkday(futureDays[d])) continue;
            if (monthlyBuckets[format(futureDays[d], 'yyyy-MM')]) {
              addMonthValue(monthlyBuckets, futureDays[d], dailyHours);
            }
          }
        }
      }
    }

    const resourceCosts = [];
    let totalCost = 0;
    let missingCsr = false;
    const resourceIds = Object.keys(resourceTotals);
    for (let r = 0; r < resourceIds.length; r++) {
      const resourceId = resourceIds[r];
      const resource = resourceMap[resourceId] || { name: 'Risorsa non trovata' };
      const csr = Number(resource.csr);
      const workedDays = resourceTotals[resourceId].hours / HOURS_PER_DAY;
      const cost = Number.isFinite(csr) ? workedDays * csr : 0;
      if (!Number.isFinite(csr)) missingCsr = true;
      totalCost += cost;
      resourceCosts.push({
        resourceId: resourceId,
        name: resource.name,
        csr: Number.isFinite(csr) ? csr : null,
        workedDays: workedDays,
        cost: cost
      });
    }

    resourceCosts.sort(function(a, b) {
      return a.name.localeCompare(b.name, 'it', { sensitivity: 'base' });
    });

    const saleAmount = Number(project.saleAmount);
    const estimatedDays = Number(project.estimatedRealDays);
    const realDays = realHours / HOURS_PER_DAY;
    const margin = Number.isFinite(saleAmount) ? saleAmount - totalCost : null;
    const marginPercent = Number.isFinite(saleAmount) && saleAmount !== 0 ? (margin / saleAmount) * 100 : null;
    const estimateDiffPercent = Number.isFinite(estimatedDays) && estimatedDays !== 0
      ? ((realDays - estimatedDays) / estimatedDays) * 100
      : null;

    if (Number.isFinite(estimatedDays)) crossEstimatedDays += estimatedDays;
    if (Number.isFinite(saleAmount)) crossSaleAmount += saleAmount;
    crossTotalCost += totalCost;
    crossRealHours += realHours;
    crossFutureHours += futureHours;

    projectRows.push({
      id: project.id,
      name: project.name,
      endDate: projectEnd,
      futureDays: futureHours / HOURS_PER_DAY,
      saleAmount: Number.isFinite(saleAmount) ? saleAmount : null,
      totalCost: totalCost,
      margin: margin,
      marginPercent: marginPercent,
      estimatedDays: Number.isFinite(estimatedDays) ? estimatedDays : null,
      realDays: realDays,
      estimateDiffPercent: estimateDiffPercent,
      missingCsr: missingCsr,
      resourceCosts: resourceCosts
    });
  }

  projectRows.sort(function(a, b) {
    if (!a.endDate && !b.endDate) return a.name.localeCompare(b.name, 'it', { sensitivity: 'base' });
    if (!a.endDate) return 1;
    if (!b.endDate) return -1;
    return a.endDate.getTime() - b.endDate.getTime();
  });

  const monthlyBucketRows = Object.values(monthlyBuckets);
  monthlyBucketRows.sort(function(a, b) {
    return a.key.localeCompare(b.key);
  });

  const monthlyRows = [];
  for (let i = 0; i < monthlyBucketRows.length; i++) {
    const row = monthlyBucketRows[i];
    monthlyRows.push({
      key: row.key,
      label: row.label,
      days: row.hours / HOURS_PER_DAY
    });
  }

  const crossRealDays = crossRealHours / HOURS_PER_DAY;
  const crossMargin = crossSaleAmount - crossTotalCost;
  const crossMarginPercent = crossSaleAmount !== 0 ? (crossMargin / crossSaleAmount) * 100 : null;
  const crossDiffPercent = crossEstimatedDays !== 0
    ? ((crossRealDays - crossEstimatedDays) / crossEstimatedDays) * 100
    : null;

  return {
    calculatedAt: today,
    maxEndDate: maxEndDate,
    kpis: {
      crossFutureDays: crossFutureHours / HOURS_PER_DAY,
      crossSaleAmount: crossSaleAmount,
      crossTotalCost: crossTotalCost,
      crossMargin: crossMargin,
      crossMarginPercent: crossMarginPercent,
      crossEstimatedDays: crossEstimatedDays,
      crossRealDays: crossRealDays,
      crossDiffPercent: crossDiffPercent
    },
    projects: projectRows,
    monthlyRows: monthlyRows
  };
};

const sumField = function(rows, field) {
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    const value = rows[i][field];
    if (Number.isFinite(value)) total += value;
  }
  return total;
};

const EUR_FMT = '"€" #,##0.00';
const PCT_FMT = '0.0"%"';
const NUM_FMT = '#,##0.00';

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { fgColor: { rgb: '4F46E5' } },
  alignment: { horizontal: 'center', vertical: 'center' },
  border: {
    top: { style: 'thin', color: { rgb: 'D1D5DB' } },
    bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
    left: { style: 'thin', color: { rgb: 'D1D5DB' } },
    right: { style: 'thin', color: { rgb: 'D1D5DB' } }
  }
};

const TOTAL_ROW_STYLE = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'E5E7EB' } },
  border: {
    top: { style: 'medium', color: { rgb: '9CA3AF' } }
  }
};

const ZEBRA_FILL = { fgColor: { rgb: 'F9FAFB' } };

const setCellStyle = function(sheet, ref, partial) {
  if (!sheet[ref]) return;
  sheet[ref].s = Object.assign({}, sheet[ref].s || {}, partial);
};

const applyHeaderStyle = function(sheet, columnCount) {
  for (let c = 0; c < columnCount; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: c });
    if (sheet[cellRef]) sheet[cellRef].s = HEADER_STYLE;
  }
};

const applyColumnFormat = function(sheet, colIndex, startRow, endRow, fmt) {
  for (let r = startRow; r <= endRow; r++) {
    const ref = XLSX.utils.encode_cell({ r: r, c: colIndex });
    if (sheet[ref] && typeof sheet[ref].v === 'number') {
      sheet[ref].z = fmt;
      setCellStyle(sheet, ref, { numFmt: fmt });
    }
  }
};

const applyZebraRows = function(sheet, columnCount, dataStartRow, dataEndRow) {
  for (let r = dataStartRow; r <= dataEndRow; r++) {
    if ((r - dataStartRow) % 2 !== 1) continue;
    for (let c = 0; c < columnCount; c++) {
      const ref = XLSX.utils.encode_cell({ r: r, c: c });
      if (sheet[ref]) setCellStyle(sheet, ref, { fill: ZEBRA_FILL });
    }
  }
};

const applyTotalRowStyle = function(sheet, columnCount, rowIndex) {
  for (let c = 0; c < columnCount; c++) {
    const ref = XLSX.utils.encode_cell({ r: rowIndex, c: c });
    if (sheet[ref]) setCellStyle(sheet, ref, TOTAL_ROW_STYLE);
  }
};

const freezeHeader = function(sheet) {
  sheet['!freeze'] = { xSplit: 0, ySplit: 1 };
  sheet['!views'] = [{ state: 'frozen', ySplit: 1, topLeftCell: 'A2' }];
};

const exportAnalysisToExcel = function(analysis) {
  if (!analysis) return;

  const workbook = XLSX.utils.book_new();
  const kpis = analysis.kpis;

  // --- KPI sheet ---
  const kpiData = [
    ['KPI', 'Valore'],
    ['Data calcolo', format(analysis.calculatedAt, 'dd/MM/yyyy')],
    ['Data fine progetto maggiore', analysis.maxEndDate ? format(analysis.maxEndDate, 'dd/MM/yyyy') : 'n/d'],
    ['Giornate da erogare cross progetto', rawNumber(kpis.crossFutureDays)],
    ['Totale Venduto', rawNumber(kpis.crossSaleAmount)],
    ['Costo Risorse Totale', rawNumber(kpis.crossTotalCost)],
    ['Margine Totale', rawNumber(kpis.crossMargin)],
    ['Margine Totale %', rawNumber(kpis.crossMarginPercent)],
    ['Totale Giornate Reali', rawNumber(kpis.crossRealDays)],
    ['Totale Giornate Stimate', rawNumber(kpis.crossEstimatedDays)],
    ['Differenza % Reali/Stimate', rawNumber(kpis.crossDiffPercent)]
  ];
  const kpiSheet = XLSX.utils.aoa_to_sheet(kpiData);
  applyHeaderStyle(kpiSheet, 2);
  kpiSheet['!cols'] = [{ wch: 36 }, { wch: 22 }];
  // Row indices (0-based): 3 future days, 4 venduto, 5 costo, 6 margine, 7 margine%, 8 reali, 9 stimate, 10 diff%
  applyColumnFormat(kpiSheet, 1, 3, 3, NUM_FMT);
  applyColumnFormat(kpiSheet, 1, 4, 6, EUR_FMT);
  applyColumnFormat(kpiSheet, 1, 7, 7, PCT_FMT);
  applyColumnFormat(kpiSheet, 1, 8, 9, NUM_FMT);
  applyColumnFormat(kpiSheet, 1, 10, 10, PCT_FMT);
  applyZebraRows(kpiSheet, 2, 1, kpiData.length - 1);
  freezeHeader(kpiSheet);
  XLSX.utils.book_append_sheet(workbook, kpiSheet, 'KPI');

  // --- Progetti sheet ---
  const projectData = [[
    'Progetto',
    'Data Fine',
    'Giornate da erogare',
    'Importo venduto',
    'Costo risorse',
    'Margine',
    'Margine %',
    'Giornate stimate',
    'Giornate reali',
    'Diff. % Reali/Stimate',
    'CSR mancante'
  ]];
  let totFuture = 0;
  let totSale = 0;
  let totCost = 0;
  let totMargin = 0;
  let totEstimated = 0;
  let totReal = 0;
  for (let i = 0; i < analysis.projects.length; i++) {
    const project = analysis.projects[i];
    if (Number.isFinite(project.futureDays)) totFuture += project.futureDays;
    if (Number.isFinite(project.saleAmount)) totSale += project.saleAmount;
    if (Number.isFinite(project.totalCost)) totCost += project.totalCost;
    if (Number.isFinite(project.margin)) totMargin += project.margin;
    if (Number.isFinite(project.estimatedDays)) totEstimated += project.estimatedDays;
    if (Number.isFinite(project.realDays)) totReal += project.realDays;
    projectData.push([
      project.name,
      project.endDate ? format(project.endDate, 'dd/MM/yyyy') : '',
      rawNumber(project.futureDays),
      rawNumber(project.saleAmount),
      rawNumber(project.totalCost),
      rawNumber(project.margin),
      rawNumber(project.marginPercent),
      rawNumber(project.estimatedDays),
      rawNumber(project.realDays),
      rawNumber(project.estimateDiffPercent),
      project.missingCsr ? 'Si' : 'No'
    ]);
  }
  const projectDataLastRow = projectData.length - 1;
  if (analysis.projects.length > 0) {
    projectData.push([
      'Totale',
      '',
      totFuture,
      totSale,
      totCost,
      totMargin,
      '',
      totEstimated,
      totReal,
      '',
      ''
    ]);
  }
  const projectSheet = XLSX.utils.aoa_to_sheet(projectData);
  applyHeaderStyle(projectSheet, 11);
  projectSheet['!cols'] = [
    { wch: 32 },
    { wch: 14 },
    { wch: 20 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 14 },
    { wch: 18 },
    { wch: 18 },
    { wch: 22 },
    { wch: 14 }
  ];
  if (analysis.projects.length > 0) {
    // Formats on data rows + total row
    const lastFmtRow = projectData.length - 1;
    applyColumnFormat(projectSheet, 2, 1, lastFmtRow, NUM_FMT);
    applyColumnFormat(projectSheet, 3, 1, lastFmtRow, EUR_FMT);
    applyColumnFormat(projectSheet, 4, 1, lastFmtRow, EUR_FMT);
    applyColumnFormat(projectSheet, 5, 1, lastFmtRow, EUR_FMT);
    applyColumnFormat(projectSheet, 6, 1, lastFmtRow, PCT_FMT);
    applyColumnFormat(projectSheet, 7, 1, lastFmtRow, NUM_FMT);
    applyColumnFormat(projectSheet, 8, 1, lastFmtRow, NUM_FMT);
    applyColumnFormat(projectSheet, 9, 1, lastFmtRow, PCT_FMT);
    applyZebraRows(projectSheet, 11, 1, projectDataLastRow);
    applyTotalRowStyle(projectSheet, 11, projectData.length - 1);
  }
  freezeHeader(projectSheet);
  XLSX.utils.book_append_sheet(workbook, projectSheet, 'Progetti');

  // --- Costi Risorse sheet ---
  const resourceData = [[
    'Progetto',
    'Risorsa',
    'CSR',
    'Giornate lavorate',
    'Costo'
  ]];
  for (let p = 0; p < analysis.projects.length; p++) {
    const project = analysis.projects[p];
    const resourceCosts = project.resourceCosts || [];
    for (let r = 0; r < resourceCosts.length; r++) {
      const resource = resourceCosts[r];
      resourceData.push([
        project.name,
        resource.name,
        rawNumber(resource.csr),
        rawNumber(resource.workedDays),
        rawNumber(resource.cost)
      ]);
    }
  }
  const resourceSheet = XLSX.utils.aoa_to_sheet(resourceData);
  applyHeaderStyle(resourceSheet, 5);
  resourceSheet['!cols'] = [
    { wch: 32 },
    { wch: 28 },
    { wch: 14 },
    { wch: 20 },
    { wch: 18 }
  ];
  if (resourceData.length > 1) {
    applyColumnFormat(resourceSheet, 2, 1, resourceData.length - 1, EUR_FMT);
    applyColumnFormat(resourceSheet, 3, 1, resourceData.length - 1, NUM_FMT);
    applyColumnFormat(resourceSheet, 4, 1, resourceData.length - 1, EUR_FMT);
    applyZebraRows(resourceSheet, 5, 1, resourceData.length - 1);
  }
  freezeHeader(resourceSheet);
  XLSX.utils.book_append_sheet(workbook, resourceSheet, 'Costi Risorse');

  // --- Mensile sheet ---
  const monthlyData = [['Mese', 'Giornate da erogare']];
  let totMonthlyDays = 0;
  for (let m = 0; m < analysis.monthlyRows.length; m++) {
    const row = analysis.monthlyRows[m];
    if (Number.isFinite(row.days)) totMonthlyDays += row.days;
    monthlyData.push([row.label, rawNumber(row.days)]);
  }
  const monthlyDataLastRow = monthlyData.length - 1;
  if (analysis.monthlyRows.length > 0) {
    monthlyData.push(['Totale', totMonthlyDays]);
  }
  const monthlySheet = XLSX.utils.aoa_to_sheet(monthlyData);
  applyHeaderStyle(monthlySheet, 2);
  monthlySheet['!cols'] = [{ wch: 14 }, { wch: 22 }];
  if (analysis.monthlyRows.length > 0) {
    applyColumnFormat(monthlySheet, 1, 1, monthlyData.length - 1, NUM_FMT);
    applyZebraRows(monthlySheet, 2, 1, monthlyDataLastRow);
    applyTotalRowStyle(monthlySheet, 2, monthlyData.length - 1);
  }
  freezeHeader(monthlySheet);
  XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Mensile');

  const filename = 'analisi-progetti-' + format(new Date(), 'yyyyMMdd-HHmm') + '.xlsx';
  XLSX.writeFile(workbook, filename);
};

// ============================================================
// PPTX export
// ============================================================

const PPT_BRAND_BLUE = '0072BC';
const PPT_DARK = '1F2937';
const PPT_MUTED = '6B7280';
const PPT_LIGHT_BG = 'F9FAFB';
const PPT_WHITE = 'FFFFFF';
const PPT_GREEN = '16A34A';
const PPT_RED = 'DC2626';
const PPT_SLATE = '94A3B8';
const PPT_GREY_BORDER = 'E5E7EB';

const SLIDE_W = 13.333;
const SLIDE_H = 7.5;

const MESI_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const formatMonthIT = function(date) {
  return MESI_IT[date.getMonth()] + ' ' + date.getFullYear();
};

const safeNumber = function(value) {
  return Number.isFinite(value) ? value : 0;
};

const pptCurrency = function(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return value.toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2
  });
};

const pptNumber = function(value, digits) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return value.toLocaleString('it-IT', {
    minimumFractionDigits: digits, maximumFractionDigits: digits
  });
};

const pptPercent = function(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/d';
  return pptNumber(value, 1) + '%';
};

const signColor = function(value, neutral) {
  if (value === null || value === undefined || !Number.isFinite(value)) return neutral || PPT_DARK;
  return value >= 0 ? PPT_GREEN : PPT_RED;
};

const diffSignColor = function(value, neutral) {
  if (value === null || value === undefined || !Number.isFinite(value)) return neutral || PPT_DARK;
  return value <= 0 ? PPT_GREEN : PPT_RED;
};

const defineMasters = function(pres, analysis) {
  const footerText = 'Analisi Progetti · ' + formatMonthIT(analysis.calculatedAt);

  pres.defineSlideMaster({
    title: 'BASE',
    background: { color: PPT_WHITE },
    objects: [
      { rect: { x: 0, y: 0, w: SLIDE_W, h: 0.08, fill: { color: PPT_BRAND_BLUE } } },
      { line: { x: 0.5, y: SLIDE_H - 0.45, w: SLIDE_W - 1, h: 0, line: { color: PPT_GREY_BORDER, width: 0.75 } } },
      {
        text: {
          text: footerText,
          options: { x: 0.5, y: SLIDE_H - 0.4, w: SLIDE_W - 1, h: 0.3, fontSize: 9, color: PPT_MUTED, fontFace: 'Calibri', align: 'left', valign: 'middle' }
        }
      }
    ],
    slideNumber: { x: SLIDE_W - 1.0, y: SLIDE_H - 0.4, w: 0.5, h: 0.3, fontSize: 9, color: PPT_MUTED, fontFace: 'Calibri', align: 'right', valign: 'middle' }
  });

  pres.defineSlideMaster({
    title: 'COVER',
    background: { color: PPT_WHITE },
    objects: [
      { rect: { x: 0, y: 0, w: 0.4, h: SLIDE_H, fill: { color: PPT_BRAND_BLUE } } }
    ]
  });
};

const addCoverSlide = function(pres, analysis) {
  const slide = pres.addSlide({ masterName: 'COVER' });
  slide.addText('Analisi Progetti', {
    x: 1, y: 2.5, w: 11, h: 1,
    fontSize: 48, bold: true, color: PPT_DARK, fontFace: 'Calibri'
  });
  slide.addText('Report di ' + formatMonthIT(analysis.calculatedAt), {
    x: 1, y: 3.6, w: 11, h: 0.7,
    fontSize: 28, color: PPT_BRAND_BLUE, fontFace: 'Calibri'
  });
  slide.addText('Calcolato il ' + format(analysis.calculatedAt, 'dd/MM/yyyy'), {
    x: 1, y: 6.5, w: 11, h: 0.4,
    fontSize: 12, color: PPT_MUTED, fontFace: 'Calibri'
  });
};

const addSlideTitle = function(slide, text) {
  slide.addText(text, {
    x: 0.5, y: 0.25, w: 12, h: 0.5,
    fontSize: 24, bold: true, color: PPT_DARK, fontFace: 'Calibri'
  });
};

const addKpiCard = function(slide, opts) {
  slide.addShape('rect', {
    x: opts.x, y: opts.y, w: opts.w, h: opts.h,
    fill: { color: PPT_WHITE },
    line: { color: PPT_GREY_BORDER, width: 0.75 }
  });
  slide.addShape('rect', {
    x: opts.x, y: opts.y, w: opts.w, h: 0.06,
    fill: { color: PPT_BRAND_BLUE },
    line: { type: 'none' }
  });
  slide.addText(opts.label, {
    x: opts.x + 0.15, y: opts.y + 0.18, w: opts.w - 0.3, h: 0.35,
    fontSize: 10, color: PPT_MUTED, fontFace: 'Calibri', bold: true, charSpacing: 2
  });
  slide.addText(opts.value, {
    x: opts.x + 0.15, y: opts.y + 0.7, w: opts.w - 0.3, h: opts.h - 0.85,
    fontSize: 24, bold: true, color: opts.color || PPT_DARK, fontFace: 'Calibri', valign: 'middle'
  });
};

const addExecutiveSummarySlide = function(pres, analysis) {
  const slide = pres.addSlide({ masterName: 'BASE' });
  addSlideTitle(slide, 'Executive Summary');

  const kpis = analysis.kpis;
  const cardW = 3.0;
  const cardH = 2.0;
  const gap = 0.13;
  const totalW = cardW * 4 + gap * 3;
  const startX = (SLIDE_W - totalW) / 2;
  const startY1 = 1.1;
  const startY2 = startY1 + cardH + 0.4;

  const row1 = [
    { label: 'TOTALE VENDUTO', value: pptCurrency(kpis.crossSaleAmount) },
    { label: 'COSTO RISORSE', value: pptCurrency(kpis.crossTotalCost) },
    { label: 'MARGINE TOTALE', value: pptCurrency(kpis.crossMargin), color: signColor(kpis.crossMargin) },
    { label: 'MARGINE TOTALE %', value: pptPercent(kpis.crossMarginPercent), color: signColor(kpis.crossMarginPercent) }
  ];
  const row2 = [
    { label: 'GIORNATE DA EROGARE', value: pptNumber(kpis.crossFutureDays, 2) },
    { label: 'GIORNATE STIMATE', value: pptNumber(kpis.crossEstimatedDays, 2) },
    { label: 'GIORNATE REALI', value: pptNumber(kpis.crossRealDays, 2) },
    { label: 'DIFF. % REALI/STIMATE', value: pptPercent(kpis.crossDiffPercent), color: diffSignColor(kpis.crossDiffPercent) }
  ];

  for (let i = 0; i < row1.length; i++) {
    addKpiCard(slide, Object.assign({}, row1[i], { x: startX + i * (cardW + gap), y: startY1, w: cardW, h: cardH }));
  }
  for (let i = 0; i < row2.length; i++) {
    addKpiCard(slide, Object.assign({}, row2[i], { x: startX + i * (cardW + gap), y: startY2, w: cardW, h: cardH }));
  }

  if (analysis.maxEndDate) {
    slide.addText('Periodo analizzato fino al ' + format(analysis.maxEndDate, 'dd/MM/yyyy'), {
      x: 0.5, y: SLIDE_H - 0.9, w: 12, h: 0.3,
      fontSize: 10, italic: true, color: PPT_MUTED, fontFace: 'Calibri', align: 'center'
    });
  }
};

const addMonthlyChartSlide = function(pres, analysis) {
  const slide = pres.addSlide({ masterName: 'BASE' });
  addSlideTitle(slide, 'Carico di lavoro previsto per mese');

  if (!analysis.monthlyRows || analysis.monthlyRows.length === 0) {
    slide.addText('Nessun carico futuro disponibile.', {
      x: 0.5, y: 3, w: 12, h: 1, fontSize: 16, color: PPT_MUTED, align: 'center', fontFace: 'Calibri'
    });
    return;
  }

  const labels = [];
  const values = [];
  for (let i = 0; i < analysis.monthlyRows.length; i++) {
    labels.push(analysis.monthlyRows[i].label);
    values.push(safeNumber(analysis.monthlyRows[i].days));
  }

  slide.addChart(pres.ChartType.bar, [{ name: 'Giornate da erogare', labels: labels, values: values }], {
    x: 0.5, y: 1.1, w: SLIDE_W - 1, h: 5.6,
    barDir: 'col', barGrouping: 'clustered',
    chartColors: [PPT_BRAND_BLUE],
    showValue: true,
    dataLabelColor: PPT_DARK,
    dataLabelFontSize: 9,
    dataLabelFormatCode: '#,##0.00',
    catAxisLabelFontSize: 10,
    catAxisLabelColor: PPT_DARK,
    valAxisLabelFontSize: 10,
    valAxisLabelColor: PPT_DARK,
    showLegend: false,
    showTitle: false
  });
};

const buildProjectTableRows = function(chunk) {
  const headerCells = [
    { text: 'Progetto', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'left' } },
    { text: 'Fine', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'center' } },
    { text: 'Venduto', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'right' } },
    { text: 'Costo', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'right' } },
    { text: 'Margine', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'right' } },
    { text: 'Margine %', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'right' } },
    { text: 'Stimate', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'right' } },
    { text: 'Reali', options: { bold: true, color: PPT_WHITE, fill: { color: PPT_BRAND_BLUE }, valign: 'middle', align: 'right' } }
  ];
  const rows = [headerCells];
  for (let i = 0; i < chunk.length; i++) {
    const p = chunk[i];
    const stripe = i % 2 === 1 ? PPT_LIGHT_BG : PPT_WHITE;
    rows.push([
      { text: p.name, options: { color: PPT_DARK, fill: { color: stripe }, valign: 'middle', align: 'left' } },
      { text: p.endDate ? format(p.endDate, 'dd/MM/yyyy') : 'n/d', options: { color: PPT_DARK, fill: { color: stripe }, valign: 'middle', align: 'center' } },
      { text: pptCurrency(p.saleAmount), options: { color: PPT_DARK, fill: { color: stripe }, valign: 'middle', align: 'right' } },
      { text: pptCurrency(p.totalCost), options: { color: PPT_DARK, fill: { color: stripe }, valign: 'middle', align: 'right' } },
      { text: pptCurrency(p.margin), options: { color: signColor(p.margin), bold: true, fill: { color: stripe }, valign: 'middle', align: 'right' } },
      { text: pptPercent(p.marginPercent), options: { color: signColor(p.marginPercent), fill: { color: stripe }, valign: 'middle', align: 'right' } },
      { text: pptNumber(p.estimatedDays, 2), options: { color: PPT_DARK, fill: { color: stripe }, valign: 'middle', align: 'right' } },
      { text: pptNumber(p.realDays, 2), options: { color: PPT_DARK, fill: { color: stripe }, valign: 'middle', align: 'right' } }
    ]);
  }
  return rows;
};

const addProjectTableSlides = function(pres, analysis) {
  const projects = analysis.projects || [];
  if (projects.length === 0) {
    const slide = pres.addSlide({ masterName: 'BASE' });
    addSlideTitle(slide, 'Dettaglio Progetti');
    slide.addText('Nessun progetto disponibile.', {
      x: 0.5, y: 3, w: 12, h: 1, fontSize: 16, color: PPT_MUTED, align: 'center', fontFace: 'Calibri'
    });
    return;
  }

  const pageSize = 10;
  const totalPages = Math.ceil(projects.length / pageSize);
  const colW = [3.8, 1.1, 1.5, 1.4, 1.5, 1.05, 1.05, 1.05];

  for (let page = 0; page < totalPages; page++) {
    const slide = pres.addSlide({ masterName: 'BASE' });
    const titleSuffix = totalPages > 1 ? ' (' + (page + 1) + '/' + totalPages + ')' : '';
    addSlideTitle(slide, 'Dettaglio Progetti' + titleSuffix);

    const chunk = projects.slice(page * pageSize, (page + 1) * pageSize);
    const rows = buildProjectTableRows(chunk);

    slide.addTable(rows, {
      x: 0.5, y: 1.1, w: SLIDE_W - 1,
      colW: colW,
      rowH: 0.38,
      fontSize: 10,
      fontFace: 'Calibri',
      border: { type: 'solid', color: PPT_GREY_BORDER, pt: 0.5 },
      autoPage: false
    });
  }
};

const addMarginChartSlide = function(pres, analysis) {
  const slide = pres.addSlide({ masterName: 'BASE' });
  addSlideTitle(slide, 'Margine per progetto');

  const projects = (analysis.projects || []).filter(function(p) { return Number.isFinite(p.margin); });
  if (projects.length === 0) {
    slide.addText('Nessun dato di margine disponibile.', {
      x: 0.5, y: 3, w: 12, h: 1, fontSize: 16, color: PPT_MUTED, align: 'center', fontFace: 'Calibri'
    });
    return;
  }

  const sorted = projects.slice().sort(function(a, b) { return b.margin - a.margin; });
  const labels = [];
  const values = [];
  const colors = [];
  for (let i = 0; i < sorted.length; i++) {
    labels.push(sorted[i].name);
    values.push(sorted[i].margin);
    colors.push(sorted[i].margin >= 0 ? PPT_GREEN : PPT_RED);
  }

  slide.addChart(pres.ChartType.bar, [{ name: 'Margine', labels: labels, values: values }], {
    x: 0.5, y: 1.1, w: SLIDE_W - 1, h: 5.6,
    barDir: 'bar', barGrouping: 'clustered',
    chartColors: colors,
    showValue: true,
    dataLabelColor: PPT_DARK,
    dataLabelFontSize: 9,
    dataLabelFormatCode: '"€" #,##0',
    catAxisLabelFontSize: 9,
    catAxisLabelColor: PPT_DARK,
    valAxisLabelFontSize: 9,
    valAxisLabelColor: PPT_DARK,
    showLegend: false,
    showTitle: false
  });
};

const addRealVsEstimatedChartSlide = function(pres, analysis) {
  const slide = pres.addSlide({ masterName: 'BASE' });
  addSlideTitle(slide, 'Giornate Reali vs Stimate per progetto');

  const projects = (analysis.projects || []).filter(function(p) {
    return Number.isFinite(p.estimatedDays) && p.estimatedDays > 0;
  });
  if (projects.length === 0) {
    slide.addText('Nessun progetto con giornate stimate disponibile.', {
      x: 0.5, y: 3, w: 12, h: 1, fontSize: 16, color: PPT_MUTED, align: 'center', fontFace: 'Calibri'
    });
    return;
  }

  const labels = [];
  const realValues = [];
  const estimatedValues = [];
  for (let i = 0; i < projects.length; i++) {
    labels.push(projects[i].name);
    realValues.push(safeNumber(projects[i].realDays));
    estimatedValues.push(safeNumber(projects[i].estimatedDays));
  }

  slide.addChart(pres.ChartType.bar, [
    { name: 'Reali', labels: labels, values: realValues },
    { name: 'Stimate', labels: labels, values: estimatedValues }
  ], {
    x: 0.5, y: 1.1, w: SLIDE_W - 1, h: 5.6,
    barDir: 'bar', barGrouping: 'clustered',
    chartColors: [PPT_BRAND_BLUE, PPT_SLATE],
    showValue: true,
    dataLabelColor: PPT_DARK,
    dataLabelFontSize: 8,
    dataLabelFormatCode: '#,##0.0',
    catAxisLabelFontSize: 9,
    catAxisLabelColor: PPT_DARK,
    valAxisLabelFontSize: 9,
    valAxisLabelColor: PPT_DARK,
    showLegend: true,
    legendPos: 'b',
    legendFontSize: 10,
    showTitle: false
  });
};

const addOutlookSlide = function(pres, analysis) {
  const slide = pres.addSlide({ masterName: 'BASE' });
  addSlideTitle(slide, 'Note e Outlook');

  const projects = analysis.projects || [];
  let missingCsrCount = 0;
  for (let i = 0; i < projects.length; i++) {
    if (projects[i].missingCsr) missingCsrCount += 1;
  }

  const bullets = [
    { text: 'Periodo analizzato: fino al ' + (analysis.maxEndDate ? format(analysis.maxEndDate, 'dd/MM/yyyy') : 'n/d'), options: { bullet: true, fontSize: 14, color: PPT_DARK, fontFace: 'Calibri' } },
    { text: 'Totale progetti monitorati: ' + projects.length, options: { bullet: true, fontSize: 14, color: PPT_DARK, fontFace: 'Calibri' } },
    { text: 'Progetti senza CSR completo: ' + missingCsrCount, options: { bullet: true, fontSize: 14, color: missingCsrCount > 0 ? PPT_RED : PPT_DARK, fontFace: 'Calibri' } },
    { text: 'Totale giornate ancora da erogare: ' + pptNumber(analysis.kpis.crossFutureDays, 2), options: { bullet: true, fontSize: 14, color: PPT_DARK, fontFace: 'Calibri' } }
  ];

  slide.addText('Riepilogo', {
    x: 0.5, y: 1.0, w: 6, h: 0.5,
    fontSize: 16, bold: true, color: PPT_BRAND_BLUE, fontFace: 'Calibri'
  });
  slide.addText(bullets, {
    x: 0.5, y: 1.6, w: 6, h: 4,
    valign: 'top', paraSpaceAfter: 8
  });

  slide.addShape('rect', {
    x: 7.0, y: 1.0, w: SLIDE_W - 7.5, h: 5.6,
    fill: { color: PPT_LIGHT_BG },
    line: { color: PPT_GREY_BORDER, width: 0.75 }
  });
  slide.addText('Note', {
    x: 7.2, y: 1.15, w: SLIDE_W - 7.9, h: 0.4,
    fontSize: 14, bold: true, color: PPT_BRAND_BLUE, fontFace: 'Calibri'
  });

  slide.addText('Generato il ' + format(new Date(), 'dd/MM/yyyy HH:mm'), {
    x: 0.5, y: SLIDE_H - 0.9, w: 12, h: 0.3,
    fontSize: 9, italic: true, color: PPT_MUTED, fontFace: 'Calibri', align: 'left'
  });
};

const exportAnalysisToPptx = function(analysis) {
  if (!analysis) return;
  const pres = new pptxgen();
  pres.layout = 'LAYOUT_WIDE';
  pres.title = 'Analisi Progetti';
  pres.company = 'Analisi Progetti';

  defineMasters(pres, analysis);
  addCoverSlide(pres, analysis);
  addExecutiveSummarySlide(pres, analysis);
  addMonthlyChartSlide(pres, analysis);
  addProjectTableSlides(pres, analysis);
  addMarginChartSlide(pres, analysis);
  addRealVsEstimatedChartSlide(pres, analysis);
  addOutlookSlide(pres, analysis);

  const filename = 'analisi-progetti-' + format(new Date(), 'yyyyMMdd-HHmm') + '.pptx';
  pres.writeFile({ fileName: filename });
};

export const ProjectAnalysisTab = ({ projects, tasks, resources }) => {
  const [analysis, setAnalysis] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});

  const handleStartAnalysis = function() {
    setAnalysis(calculateAnalysis(projects || [], tasks || [], resources || []));
    setExpandedProjects({});
  };

  const handleExport = function() {
    exportAnalysisToExcel(analysis);
  };

  const handleExportPptx = function() {
    exportAnalysisToPptx(analysis);
  };

  const toggleProject = function(projectId) {
    setExpandedProjects(function(prev) {
      const next = Object.assign({}, prev);
      if (next[projectId]) delete next[projectId];
      else next[projectId] = true;
      return next;
    });
  };

  const monthlyRows = analysis ? analysis.monthlyRows : [];
  const projectRows = analysis ? analysis.projects : [];
  const kpis = analysis ? analysis.kpis : null;
  const monthlyTotal = sumField(monthlyRows, 'days');
  const totalFutureDays = sumField(projectRows, 'futureDays');
  const totalSaleAmount = sumField(projectRows, 'saleAmount');
  const totalCostAll = sumField(projectRows, 'totalCost');
  const totalMargin = sumField(projectRows, 'margin');
  const totalEstimated = sumField(projectRows, 'estimatedDays');
  const totalReal = sumField(projectRows, 'realDays');

  const monthlyRowEls = [];
  for (let mi = 0; mi < monthlyRows.length; mi++) {
    const row = monthlyRows[mi];
    monthlyRowEls.push(
      <TableRow key={row.key} className={mi % 2 === 1 ? 'bg-muted/20' : ''}>
        <TableCell>{row.label}</TableCell>
        <TableCell className="text-right font-medium">{formatNumber(row.days, 2)}</TableCell>
      </TableRow>
    );
  }

  const projectRowEls = [];
  for (let pi = 0; pi < projectRows.length; pi++) {
    const project = projectRows[pi];
    const isExpanded = !!expandedProjects[project.id];
    const resourceCosts = project.resourceCosts || [];
    const hasResources = resourceCosts.length > 0;
    const stripeClass = pi % 2 === 1 ? 'bg-muted/20' : '';
    const diffPercent = project.estimateDiffPercent;
    const diffColor =
      diffPercent === null || !Number.isFinite(diffPercent)
        ? ''
        : diffPercent <= 0
          ? 'text-emerald-600'
          : 'text-rose-600';

    projectRowEls.push(
      <TableRow key={project.id} className={stripeClass}>
        <TableCell className="w-8 align-top">
          {hasResources ? (
            <button
              type="button"
              onClick={function() { toggleProject(project.id); }}
              className="p-1 rounded hover:bg-muted text-muted-foreground"
              aria-label={isExpanded ? 'Comprimi' : 'Espandi'}
            >
              {isExpanded
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : null}
        </TableCell>
        <TableCell className="min-w-[220px]">
          <div className="font-medium">{project.name}</div>
          {project.missingCsr && (
            <Badge variant="outline" className="mt-1 text-amber-700 border-amber-300">
              CSR mancante
            </Badge>
          )}
        </TableCell>
        <TableCell>{project.endDate ? format(project.endDate, 'dd/MM/yyyy') : 'n/d'}</TableCell>
        <TableCell className="text-right font-medium">{formatNumber(project.futureDays, 2)}</TableCell>
        <TableCell className="text-right">{formatCurrency(project.saleAmount)}</TableCell>
        <TableCell className="text-right">{formatCurrency(project.totalCost)}</TableCell>
        <TableCell className={'text-right font-medium ' + getSignColor(project.margin)}>{formatCurrency(project.margin)}</TableCell>
        <TableCell className={'text-right ' + getSignColor(project.marginPercent)}>{formatPercent(project.marginPercent)}</TableCell>
        <TableCell className="text-right">{formatNumber(project.estimatedDays, 2)}</TableCell>
        <TableCell className="text-right">{formatNumber(project.realDays, 2)}</TableCell>
        <TableCell className={'text-right ' + diffColor}>{formatPercent(diffPercent)}</TableCell>
      </TableRow>
    );

    if (isExpanded && hasResources) {
      const resourceRowEls = [];
      for (let ri = 0; ri < resourceCosts.length; ri++) {
        const resource = resourceCosts[ri];
        resourceRowEls.push(
          <TableRow key={resource.resourceId} className={ri % 2 === 1 ? 'bg-muted/20' : ''}>
            <TableCell>{resource.name}</TableCell>
            <TableCell className="text-right">
              {resource.csr === null ? (
                <span className="text-amber-700">n/d</span>
              ) : formatCurrency(resource.csr)}
            </TableCell>
            <TableCell className="text-right">{formatNumber(resource.workedDays, 2)}</TableCell>
            <TableCell className="text-right font-medium">{formatCurrency(resource.cost)}</TableCell>
          </TableRow>
        );
      }
      projectRowEls.push(
        <TableRow key={project.id + '-detail'} className="bg-muted/30">
          <TableCell></TableCell>
          <TableCell colSpan={10} className="py-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Dettaglio costo risorse
            </div>
            <div className="rounded border border-border bg-background overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Risorsa</TableHead>
                    <TableHead className="text-right">CSR</TableHead>
                    <TableHead className="text-right">Giornate lavorate</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{resourceRowEls}</TableBody>
              </Table>
            </div>
          </TableCell>
        </TableRow>
      );
    }
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="space-y-4 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Analisi Progetti</h2>
            <p className="text-sm text-muted-foreground">
              I valori vengono calcolati solo alla pressione del pulsante.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleExport}
              variant="outline"
              className="gap-2"
              disabled={!analysis}
              data-testid="export-project-analysis-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Esporta Excel
            </Button>
            <Button
              onClick={handleExportPptx}
              variant="outline"
              className="gap-2"
              disabled={!analysis}
              data-testid="export-project-analysis-pptx-btn"
            >
              <Presentation className="w-4 h-4" />
              Esporta PPTX
            </Button>
            <Button onClick={handleStartAnalysis} className="gap-2" data-testid="start-project-analysis-btn">
              <BarChart3 className="w-4 h-4" />
              Avvia analisi
            </Button>
          </div>
        </div>

        {!analysis && (
          <div className="border border-border border-dashed rounded-lg py-12 px-6 text-center bg-card">
            <div className="mx-auto w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center mb-3">
              <BarChart3 className="w-6 h-6" />
            </div>
            <h3 className="text-base font-medium">Nessuna analisi caricata</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Avvia l'analisi per calcolare giornate da erogare, margini e scostamenti.
            </p>
            <Button onClick={handleStartAnalysis} className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Avvia analisi
            </Button>
          </div>
        )}

        {analysis && (
          <>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Economico</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <KpiCard
                  icon={Euro}
                  label="Totale Venduto"
                  value={formatCurrency(kpis.crossSaleAmount)}
                  accentClass="bg-blue-100 text-blue-700"
                />
                <KpiCard
                  icon={Wallet}
                  label="Costo Risorse Totale"
                  value={formatCurrency(kpis.crossTotalCost)}
                  accentClass="bg-slate-100 text-slate-700"
                />
                <KpiCard
                  icon={TrendingUp}
                  label="Margine Totale"
                  value={formatCurrency(kpis.crossMargin)}
                  valueClassName={getSignColor(kpis.crossMargin)}
                  accentClass={kpis.crossMargin >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}
                  footer={Number.isFinite(kpis.crossSaleAmount) ? 'su ' + formatCurrency(kpis.crossSaleAmount) + ' venduto' : null}
                />
                <KpiCard
                  icon={Percent}
                  label="Margine Totale %"
                  value={formatPercent(kpis.crossMarginPercent)}
                  valueClassName={getSignColor(kpis.crossMarginPercent)}
                  accentClass={kpis.crossMarginPercent >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}
                />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Giornate</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <KpiCard
                  icon={CalendarClock}
                  label="Giornate da erogare cross progetto"
                  value={formatNumber(kpis.crossFutureDays, 2)}
                  accentClass="bg-indigo-100 text-indigo-700"
                  footer={'Fino al ' + (analysis.maxEndDate ? format(analysis.maxEndDate, 'dd/MM/yyyy') : 'n/d')}
                />
                <KpiCard
                  icon={Clock}
                  label="Totale Giornate Reali"
                  value={formatNumber(kpis.crossRealDays, 2)}
                  accentClass="bg-sky-100 text-sky-700"
                />
                <KpiCard
                  icon={Target}
                  label="Totale Giornate Stimate"
                  value={formatNumber(kpis.crossEstimatedDays, 2)}
                  accentClass="bg-violet-100 text-violet-700"
                />
                <KpiCard
                  icon={GitCompare}
                  label="Differenza % Reali/Stimate"
                  value={formatPercent(kpis.crossDiffPercent)}
                  valueClassName={
                    kpis.crossDiffPercent === null || !Number.isFinite(kpis.crossDiffPercent)
                      ? ''
                      : kpis.crossDiffPercent <= 0
                        ? 'text-emerald-600'
                        : 'text-rose-600'
                  }
                  accentClass={
                    kpis.crossDiffPercent === null || !Number.isFinite(kpis.crossDiffPercent)
                      ? 'bg-slate-100 text-slate-600'
                      : kpis.crossDiffPercent <= 0
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-rose-100 text-rose-700'
                  }
                  footer="Reali vs Stimate"
                />
              </div>
            </div>

            <div className="border border-border rounded-lg bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
                <h3 className="font-medium">Giornate da erogare per mese</h3>
                <span className="text-xs text-muted-foreground">
                  Calcolo del {format(analysis.calculatedAt, 'dd/MM/yyyy')}
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mese</TableHead>
                    <TableHead className="text-right">Giornate da erogare</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-muted-foreground">
                        Nessun carico futuro disponibile.
                      </TableCell>
                    </TableRow>
                  )}
                  {monthlyRowEls}
                  {monthlyRows.length > 0 && (
                    <TableRow className="font-semibold border-t-2 bg-muted/40">
                      <TableCell>Totale</TableCell>
                      <TableCell className="text-right">{formatNumber(monthlyTotal, 2)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="border border-border rounded-lg bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="font-medium">Dettaglio progetti</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Clicca sulla freccia per espandere i costi delle risorse del progetto.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Progetto</TableHead>
                    <TableHead>Fine</TableHead>
                    <TableHead className="text-right">Da erogare</TableHead>
                    <TableHead className="text-right">Importo venduto</TableHead>
                    <TableHead className="text-right">Costo risorse</TableHead>
                    <TableHead className="text-right">Margine</TableHead>
                    <TableHead className="text-right">Margine %</TableHead>
                    <TableHead className="text-right">Stimate</TableHead>
                    <TableHead className="text-right">Reali</TableHead>
                    <TableHead className="text-right">Diff. %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-muted-foreground">
                        Nessun progetto disponibile.
                      </TableCell>
                    </TableRow>
                  )}
                  {projectRowEls}
                  {projectRows.length > 0 && (
                    <TableRow className="font-semibold border-t-2 bg-muted/40">
                      <TableCell></TableCell>
                      <TableCell>Totale</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right">{formatNumber(totalFutureDays, 2)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalSaleAmount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalCostAll)}</TableCell>
                      <TableCell className={'text-right ' + getSignColor(totalMargin)}>{formatCurrency(totalMargin)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                      <TableCell className="text-right">{formatNumber(totalEstimated, 2)}</TableCell>
                      <TableCell className="text-right">{formatNumber(totalReal, 2)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectAnalysisTab;
